import { showGeoPermissionGuide } from '@/components/common/GlobalLocationPermissionGuide';
import { classifyGeoError } from '@/lib/geoPermission';
/**
 * useVehicleInspections - Hooks da Vistoria (Checklist Fase 2/2b)
 *
 * Todo acesso passa por RPCs SECURITY DEFINER que validam tenant e permissão
 * por perfil (has_granular_permission). O frontend nunca lê as tabelas.
 *
 * @version 1.9.0
 * RELEASE: 2026-10-05 v1.9.0 - Tempo real do modal recarrega aceites, links e comprovante (aceite pelo link externo sem F5).
 * RELEASE: 2026-09-29 v1.8.0 - Modal atualiza com fotos do link externo: aba oculta recarrega ao
 *          voltar, invalida Central/Kanban e polling de 30 s (grants de leitura liberados).
 * RELEASE: 2026-09-28 v1.7.0 - Adiciona troca atômica do tipo e responsável da vistoria.
 * RELEASE: 2026-09-27 v1.6.0 - Ações invalidam o cache de aceites da vistoria.
 * RELEASE: 2026-09-26 v1.5.0 - Listagem por veículo recebe tipo, periodicidade e
 *          responsável configurado, executor e revisor com respectivos avatares.
 * RELEASE: 2026-09-26 v1.4.0 - Invalida Central de Vistorias e templates; realtime por veículo;
 *          histórico de revisões por item.
 * RELEASE: 2026-09-26 v1.3.0 - useInspectionRealtime: atualização em tempo real do modal.
 * RELEASE: 2026-09-26 v1.2.0 - Tipagem dos alertas antifraude e das evidências
 *          georreferenciadas por item/anexo para auditoria visual no modal.
 * RELEASE: 2026-09-26 v1.1.0 - INSPECTION_STATUS ganha campo `tone` (bolinha colorida
 *          no padrão StatusDotLabel) para o cabeçalho do modal de vistoria; `cls`
 *          mantido para compatibilidade com a listagem (VehicleInspectionsTab).
*/
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { StatusDotTone } from '@/components/ui/status-dot-label';

const logger = createLogger('useVehicleInspections');
const rpc = (name: string, args: Record<string, unknown>) => (supabase.rpc as any)(name, args);

// -----------------------------------------------------------------------------
// Tipos
// -----------------------------------------------------------------------------
export type InspectionStatus =
  | 'pending' | 'in_progress' | 'awaiting_review' | 'correction_requested'
  | 'approved' | 'rejected' | 'cancelled';

/** Rótulos e cores semânticas dos status (`tone` = bolinha do StatusDotLabel) */
export const INSPECTION_STATUS: Record<InspectionStatus, { label: string; cls: string; tone: StatusDotTone }> = {
  pending: { label: 'Pendente', cls: 'bg-muted text-muted-foreground', tone: 'neutral' },
  in_progress: { label: 'Em andamento', cls: 'bg-primary/15 text-primary', tone: 'info' },
  awaiting_review: { label: 'Aguardando revisão', cls: 'bg-accent text-accent-foreground', tone: 'warning' },
  correction_requested: { label: 'Correção solicitada', cls: 'bg-destructive/10 text-destructive', tone: 'danger' },
  approved: { label: 'Aprovada', cls: 'bg-primary text-primary-foreground', tone: 'success' },
  rejected: { label: 'Reprovada', cls: 'bg-destructive text-destructive-foreground', tone: 'danger' },
  cancelled: { label: 'Cancelada', cls: 'bg-muted text-muted-foreground line-through', tone: 'neutral' },
};

export const DEADLINE_LABEL: Record<string, string> = {
  on_time: 'No prazo',
  start_overdue: 'Início atrasado',
  finish_overdue: 'Conclusão atrasada',
  closed: '',
};

export const FRAUD_LABEL: Record<string, string> = {
  distance: 'Itens registrados em locais distantes',
  low_accuracy: 'Localização com baixa precisão',
  clock_skew: 'Relógio do aparelho divergente',
  gallery_photo: 'Foto enviada da galeria (câmera exigida)',
};

/** Evidência específica que originou um alerta antifraude. */
export interface InspectionFraudEvidence {
  kind: 'item' | 'submission';
  answer_id?: string;
  revision_id?: string;
  item_id?: string;
  item_name?: string;
  lat?: number;
  lng?: number;
  attachment_ids?: string[];
}

/** Alerta antifraude enriquecido; registros antigos podem não ter evidências. */
export interface InspectionFraudFlag {
  type: string;
  max_m?: number;
  limit_m?: number;
  accuracy_m?: number;
  skew_seconds?: number;
  item?: string;
  evidences?: InspectionFraudEvidence[];
}

export interface InspectionRow {
  id: string;
  code: number;
  template_name: string;
  version_number: number;
  status: InspectionStatus;
  deadline_status: string;
  origin: string;
  executor_name: string | null;
  executor_avatar: string | null;
  reviewer_name: string | null;
  reviewer_avatar: string | null;
  assignee_mode: 'user' | 'queue' | 'owner' | 'provider';
  assignee_name: string | null;
  assignee_avatar: string | null;
  queue_name: string | null;
  checklist_type_id: string;
  checklist_type_name: string;
  schedule_frequency: string | null;
  due_finish_at: string | null;
  created_at: string;
  finished_at: string | null;
  fraud_alerts: number;
  items_total: number;
  items_answered: number;
}

export interface InspectionItem {
  answer_id: string;
  review_status: 'pending' | 'approved' | 'rejected';
  review_reason: string | null;
  item: {
    id: string; name: string; description: string | null; field_type: string;
    is_required: boolean; requires_approval: boolean; placeholder: string | null;
    default_value: unknown; config: Record<string, any>; version_section_id: string | null;
  };
  current: { id: string; value: any; revision_number: number; geo_lat: number | null; geo_lng: number | null; created_at: string } | null;
  revisions_count: number;
  attachments: {
    id: string; storage_path: string; file_name: string; mime_type: string | null;
    geo_lat: number | null; geo_lng: number | null; size_bytes?: number | null;
    sha256?: string | null; from_camera?: boolean | null;
  }[];
}

export interface InspectionDetail {
  instance: Record<string, any> & { status: InspectionStatus; fraud_flags: InspectionFraudFlag[]; is_executor: boolean };
  // v1.7.0: latest_version_number/has_newer_version alimentam o botão "Atualizar versão"
  version: { version_number: number; settings: Record<string, any>; latest_version_number?: number | null; has_newer_version?: boolean };
  vehicle: Record<string, any>;
  sections: { id: string; name: string; sort_order: number }[];
  items: InspectionItem[];
}

// -----------------------------------------------------------------------------
// Geolocalização (usada por item e/ou no envio, conforme o modelo)
// -----------------------------------------------------------------------------
export function captureGeo(): Promise<Record<string, unknown> | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({
        lat: p.coords.latitude, lng: p.coords.longitude,
        accuracy: p.coords.accuracy, captured_at: new Date().toISOString(),
      }),
      // v2026-10-22: falha abre o guia global de permissão (resultado continua null).
      (e) => { showGeoPermissionGuide(classifyGeoError(e.code)); resolve(null); },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 },
    );
  });
}

// -----------------------------------------------------------------------------
// Consultas
// -----------------------------------------------------------------------------
export function useVehicleInspectionsList(vehicleId: string | undefined) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['vehicle-inspections', vehicleId],
    enabled: !!vehicleId && !!currentTenant?.id,
    queryFn: async () => {
      const { data, error } = await rpc('list_vehicle_checklists', { p_vehicle_id: vehicleId, p_tenant_hint: null });
      if (error) { logger.error('Erro ao listar vistorias', { error: error.message }); throw error; }
      return (data ?? []) as InspectionRow[];
    },
  });
}

export function useInspectionTemplatesForSelection(enabled: boolean) {
  return useQuery({
    queryKey: ['checklist-templates-selection'],
    enabled,
    // v1.4.0: sempre rebusca ao abrir, para exibir templates recém-publicados sem F5.
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const { data, error } = await rpc('list_checklist_templates_for_selection', { p_usage_key: 'vehicle_inspection', p_tenant_hint: null });
      if (error) throw error;
      return (data ?? []) as { id: string; name: string; version_number: number; deadline_mode: string; items_count: number; type_id: string | null; type_name: string | null }[];
    },
  });
}

export function useInspectionDetail(instanceId: string | null) {
  return useQuery({
    queryKey: ['inspection-detail', instanceId],
    enabled: !!instanceId,
    // v1.8.0: rede de segurança enquanto o modal estiver aberto
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await rpc('get_checklist_instance', { p_instance_id: instanceId, p_tenant_hint: null });
      if (error) throw error;
      return data as InspectionDetail;
    },
  });
}

export function useInspectionTimeline(instanceId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['inspection-timeline', instanceId],
    enabled: !!instanceId && enabled,
    queryFn: async () => {
      const { data, error } = await rpc('get_checklist_timeline', { p_template_id: null, p_instance_id: instanceId, p_limit: 300, p_tenant_hint: null });
      if (error) throw error;
      return (data ?? []) as { id: string; event_type: string; actor_full_name: string | null; metadata: any; created_at: string }[];
    },
  });
}

export function useVehicleSchedules(vehicleId: string | undefined) {
  return useQuery({
    queryKey: ['vehicle-inspection-schedules', vehicleId],
    enabled: !!vehicleId,
    queryFn: async () => {
      const { data, error } = await rpc('list_vehicle_checklist_schedules', { p_vehicle_id: vehicleId, p_tenant_hint: null });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });
}

// -----------------------------------------------------------------------------
// Ações (mutations genéricas com invalidação do cache)
// -----------------------------------------------------------------------------
export function useInspectionActions() {
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['vehicle-inspections'] });
    qc.invalidateQueries({ queryKey: ['inspection-detail'] });
    qc.invalidateQueries({ queryKey: ['inspection-timeline'] });
    qc.invalidateQueries({ queryKey: ['vehicle-inspection-schedules'] });
    // v1.4.0: mantém a Central de Vistorias (lista, KPIs e Kanban) sincronizada.
    qc.invalidateQueries({ queryKey: ['inspection-list'] });
    qc.invalidateQueries({ queryKey: ['inspection-kanban-column'] });
    qc.invalidateQueries({ queryKey: ['inspection-kanban-counts'] });
    qc.invalidateQueries({ queryKey: ['checklist-templates-selection'] });
    qc.invalidateQueries({ queryKey: ['vehicle-details'] });
    // v1.6.0: respostas alteram o conteúdo protegido do aceite
    qc.invalidateQueries({ queryKey: ['checklist-acceptance'] });
    // A troca de responsável revoga o link vinculado à identidade anterior.
    qc.invalidateQueries({ queryKey: ['checklist-public-link'] });
  };
  const make = (name: string, okMsg?: string) =>
    useMutation({
      mutationFn: async (args: Record<string, unknown>) => {
        const { data, error } = await rpc(name, { ...args, p_tenant_hint: null });
        if (error) throw error;
        return data;
      },
      onSuccess: () => { refresh(); if (okMsg) toast.success(okMsg); },
      onError: (e: Error) => toast.error(e.message || 'Não foi possível concluir a ação'),
    });
  return {
    create: make('create_vehicle_checklist', 'Vistoria criada'),
    start: make('start_checklist_instance', 'Vistoria iniciada'),
    saveAnswer: make('save_checklist_answer'),
    submit: make('submit_checklist_instance', 'Vistoria enviada'),
    reviewItem: make('review_checklist_item'),
    finalize: make('finalize_checklist_review', 'Revisão concluída'),
    cancel: make('cancel_checklist_instance', 'Vistoria cancelada'),
    reopen: make('reopen_checklist_instance', 'Vistoria reaberta'),
    // v1.7.0: promove a vistoria em aberto para a última versão publicada do modelo
    upgrade: make('upgrade_checklist_instance_version', 'Versão da vistoria atualizada'),
    upsertSchedule: make('upsert_checklist_schedule', 'Periodicidade salva'),
    setScheduleStatus: make('set_checklist_schedule_status', 'Periodicidade atualizada'),
    updateAssignee: make('update_checklist_assignee', 'Responsável atualizado'),
  };
}

// -----------------------------------------------------------------------------
// Tempo real (v1.3.0): uma assinatura por modal aberto. Os eventos apenas
// invalidam o cache (as RLS limitam o que cada usuário recebe) e os dados
// continuam sendo lidos via RPC. Invalidação agrupada (debounce) para evitar
// rajadas de refetch quando várias tabelas mudam na mesma operação.
// -----------------------------------------------------------------------------
const rtLog = createLogger('InspectionRealtime');
export function useInspectionRealtime(instanceId: string | null | undefined) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!instanceId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // v1.8.0: com a aba oculta apenas marca pendência; recarrega ao voltar.
    let pendingInvalidation = false;
    const flush = () => {
      pendingInvalidation = false;
      qc.invalidateQueries({ queryKey: ['inspection-detail'] });
      qc.invalidateQueries({ queryKey: ['inspection-timeline'] });
      qc.invalidateQueries({ queryKey: ['vehicle-inspections'] });
      qc.invalidateQueries({ queryKey: ['inspection-item-revisions'] });
      // v1.9.0: aceite feito pelo link externo/outro computador chega via checklist_events;
      // recarrega painel de aceites, links e comprovante sem precisar de F5.
      qc.invalidateQueries({ queryKey: ['checklist-acceptance', instanceId] });
      qc.invalidateQueries({ queryKey: ['checklist-acceptance-link', instanceId] });
      qc.invalidateQueries({ queryKey: ['checklist-public-link', instanceId] });
      qc.invalidateQueries({ queryKey: ['checklist-acceptance-receipt', instanceId] });
      // Central/Kanban de Vistorias (chaves iniciadas por "inspections-")
      qc.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith('inspections-'),
      });
    };
    const refresh = () => {
      if (document.visibilityState === 'hidden') { pendingInvalidation = true; return; }
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, 400); // agrupa várias mudanças seguidas
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && pendingInvalidation) flush();
    };
    document.addEventListener('visibilitychange', onVisibility);
    const channel = supabase
      .channel(`inspection-${instanceId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'checklist_instances', filter: `id=eq.${instanceId}` }, refresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'checklist_answers', filter: `instance_id=eq.${instanceId}` }, refresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'checklist_events', filter: `instance_id=eq.${instanceId}` }, refresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'checklist_answer_revisions' }, refresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'checklist_attachments' }, refresh)
      .subscribe((s) => rtLog.debug('status', s));
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      supabase.removeChannel(channel);
    };
  }, [instanceId, qc]);
}

// -----------------------------------------------------------------------------
// v1.4.0: tempo real da aba Vistoria do veículo (vistorias geradas pela
// periodicidade aparecem sem recarregar). Apenas invalida o cache.
// -----------------------------------------------------------------------------
export function useVehicleInspectionsRealtime(vehicleId: string | undefined) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!vehicleId) return;
    const refresh = () => {
      qc.invalidateQueries({ queryKey: ['vehicle-inspections', vehicleId] });
      qc.invalidateQueries({ queryKey: ['vehicle-inspection-schedules', vehicleId] });
      qc.invalidateQueries({ queryKey: ['vehicle-details'] });
    };
    const channel = supabase
      .channel(`vehicle-inspections-${vehicleId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'checklist_vehicle_links', filter: `vehicle_id=eq.${vehicleId}` }, refresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'checklist_schedules' }, refresh)
      .subscribe((s) => rtLog.debug('veiculo status', s));
    return () => { supabase.removeChannel(channel); };
  }, [vehicleId, qc]);
}

/** Revisão de um item (histórico append-only). */
export interface ItemRevision {
  id: string;
  revision_number: number;
  cycle_number: number | null;
  value: any;
  geo_lat: number | null;
  geo_lng: number | null;
  created_at: string;
  author_name: string | null;
  is_current: boolean;
  attachments: { id: string; storage_path: string; file_name: string; mime_type: string | null }[];
}
/** Decisão do revisor sobre o item (evento append-only). */
export interface ItemReviewEvent {
  event_type: 'item_approved' | 'item_rejected';
  reason: string | null;
  actor_name: string | null;
  created_at: string;
}

/** Histórico de revisões de um item da vistoria (v1.4.0). */
export function useItemRevisions(answerId: string | null) {
  return useQuery({
    queryKey: ['inspection-item-revisions', answerId],
    enabled: !!answerId,
    queryFn: async () => {
      const { data, error } = await rpc('get_checklist_answer_revisions', { p_answer_id: answerId, p_tenant_hint: null });
      if (error) throw error;
      return (data ?? { revisions: [], reviews: [] }) as { revisions: ItemRevision[]; reviews: ItemReviewEvent[] };
    },
  });
}

/** Envia um arquivo ao bucket privado `checklists` e devolve os metadados */
export async function uploadInspectionFile(tenantId: string, instanceId: string, answerId: string, file: File, fromCamera: boolean) {
  const buf = await file.arrayBuffer();
  const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', buf)))
    .map((b) => b.toString(16).padStart(2, '0')).join('');
  const safe = file.name.replace(/[^\w.\-]+/g, '_').slice(-120);
  const path = `${tenantId}/${instanceId}/${answerId}/${crypto.randomUUID()}-${safe}`;
  const { error } = await supabase.storage.from('checklists').upload(path, file, { upsert: false, contentType: file.type });
  if (error) throw error;
  return { storage_path: path, file_name: file.name, mime_type: file.type, size_bytes: file.size, sha256: hash, from_camera: fromCamera };
}

export async function signedInspectionUrl(path: string) {
  const { data } = await supabase.storage.from('checklists').createSignedUrl(path, 600);
  return data?.signedUrl ?? null;
}
